import { Select } from "antd"
import { ChevronDownIcon, Loader } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router"

import { useGetAccounts } from "~/hooks/accounts"
import useGlobalStore from "~/store/global"
import { IAccount } from "~/types/accounts"
import { filterOption } from "~/utils/helpers"
import { getLocationDisplay } from "~/utils/location-helpers"

export default function AgentSelectAccount({
  hideLabel,
  label,
  className = "",
  sendMessageRef,
  accountIdOverride
}: {
  hideLabel?: boolean
  label?: string
  className?: string
  sendMessageRef?: any
  /** Seed the picker's displayed value from a prop instead of `?account=`.
   *  Used by the PMB v2 workbench (containers/pre-meeting-brief-v2) so the
   *  account picker reflects the locked account without mutating the URL
   *  of the underlying page (some routes — e.g. marketing-os people lists
   *  — re-filter on `?account=` changes). User-driven changes still flow
   *  through setSearchParams. */
  accountIdOverride?: string
}) {
  const { t } = useTranslation("common")
  const featureFlags = useGlobalStore((store) => store.user?.feature_flags)
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: accountsData, isLoading: isAccountsLoading } = useGetAccounts(
    undefined,
    true
  )
  const accounts = accountsData?.data?.accounts ?? []
  const options = accounts
    .map((account: IAccount) => ({
      label:
        getLocationDisplay(account.short_name) ||
        getLocationDisplay(account.account_name),
      value: `${account.id}`
    }))
    .concat([
      { label: t("marketing_os.label_all_accounts"), value: "all_accounts" }
    ])
  const selectedAccountRaw = accountIdOverride ?? searchParams.get("account")
  // Only honor ?account= when it matches a real option. Stops Apollo
  // ObjectIds (or other non-numeric ids) leaking into the dropdown label
  // when an upstream caller wrote the wrong kind of id into the URL.
  const selectedAccount =
    selectedAccountRaw &&
    options.some((o: { value: string }) => o.value === selectedAccountRaw)
      ? selectedAccountRaw
      : undefined
  if (featureFlags?.show_only_rfp) return null
  return (
    <div
      data-testid="ask-orbitshift-account-picker"
      className={`${
        hideLabel
          ? ""
          : "border flex flex-col gap-1 rounded-xl w-full px-3 py-2 dark:bg-dark-1 border-light-border dark:border-border"
      } ${className}`}
    >
      <div className="flex items-center justify-between gap-8">
        {!hideLabel && (
          <label className="!text-13" htmlFor="account">
            {label}
          </label>
        )}
        {isAccountsLoading ? (
          <div className="w-60 h-9 rounded-xl dark:bg-dark-1 border border-light-border dark:border-border flex items-center justify-between px-3 cursor-progress">
            <p className="text-xs text-gray-400">
              {t("os_agents.label_loading_accounts")}
            </p>
            <Loader className="animate-spin" size={16} />
          </div>
        ) : (
          <Select
            filterOption={filterOption}
            options={options}
            id="account"
            className="w-60 h-9 [&_.ant-select-selector]:!rounded-xl [&_.ant-select-selector]:!bg-white dark:[&_.ant-select-selector]:!bg-dark-1 [&_.ant-select-selection-item]:text-xs"
            classNames={{
              root: "[&_.ant-select-item-option-content]:text-xs"
            }}
            placeholder={t("os_agents.placeholder_select_an_account")}
            showSearch
            suffixIcon={
              <ChevronDownIcon
                className="text-dark-2 dark:text-dark-6"
                size={16}
              />
            }
            value={selectedAccount}
            onChange={(value, option) => {
              searchParams.set("account", value)
              setSearchParams(searchParams)
              sendMessageRef?.current?.({
                tool_id: "",
                agentQuery: t("os_agents.label_got_it_analyzing_account", {
                  account_name: option.label
                }),
                accountId: value
              })
              const activeElement = document.activeElement
              if (activeElement instanceof HTMLElement) {
                activeElement.blur()
              }
            }}
          />
        )}
      </div>
    </div>
  )
}

AgentSelectAccount.displayName = "SelectAccount"
